import { useTranslation } from "react-i18next";
import { StackedBarChart } from "../../pages/dashboards/v5/components/V5Charts";
import { getThemeClasses } from "../../utils/ThemeClasses";
import {
  useReportingCompanies,
  useReportingSummary,
  computeRiskDistribution,
} from "../../features/dashboards/reporting";
import type { ReportingPlatform } from "../../features/dashboards/reportingConfig";

export default function PlatformRiskDistribution({
  platform,
  themeClasses,
}: {
  platform: ReportingPlatform;
  themeClasses: ReturnType<typeof getThemeClasses>;
}) {
  const { t } = useTranslation("entregaServicio");
  const companiesQuery = useReportingCompanies({ platform });
  const summaryQuery = useReportingSummary();

  const dist = computeRiskDistribution(companiesQuery.data ?? []);
  const totalCompanies =
    dist.buckets.healthy.companyCount +
      dist.buckets.warning.companyCount +
      dist.buckets.highRisk.companyCount +
      dist.buckets.critical.companyCount || 1;
  const cat = (b: "healthy" | "warning" | "highRisk" | "critical") => ({
    averagePercentage:
      Math.round((dist.buckets[b].companyCount / totalCompanies) * 1000) / 10,
    companyCount: dist.buckets[b].companyCount,
    units: dist.buckets[b].units,
  });

  const data = [
    {
      platform: platform.toUpperCase(),
      totalUnits:
        summaryQuery.data?.by_platform?.[platform]?.total ?? dist.totalUnits,
      healthy: cat("healthy"),
      warning: cat("warning"),
      highRisk: cat("highRisk"),
      critical: cat("critical"),
    },
  ];

  return (
    <div>
      <div className="mb-3 sm:mb-4">
        <h2 className={`text-lg sm:text-xl font-bold ${themeClasses.text}`}>
          Distribución de riesgo por cliente
        </h2>
        <p className={`text-xs sm:text-sm ${themeClasses.textSecondary}`}>
          Clientes por categoría de servicio (óptimo / advertencia / alto riesgo
          / crítico)
        </p>
      </div>
      {companiesQuery.isLoading ? (
        <div
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 sm:p-6 h-64 animate-pulse`}
        ></div>
      ) : (
        <StackedBarChart data={data} themeClasses={themeClasses} t={t} />
      )}
    </div>
  );
}
